import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Bookmark, FileText } from "lucide-react";
import VerifiedOrgMark from "./VerifiedOrgMark";
import SocialUserAvatar from "@/components/avatars/SocialUserAvatar";
import { API_BASE_URL } from "@/lib/apiBase";
import { useSocialPaths } from "@/lib/socialPaths";
import { useSocialProfile } from "@/scripts/SocialProfileContext";
import { cn } from "@/lib/utils";

/**
 * Desktop sticky personal rail for Circa Social Home (left of feed).
 */
export default function SocialHomeLeftRail({ accessToken, className }) {
  const paths = useSocialPaths();
  const { profile, complete } = useSocialProfile();
  const [mine, setMine] = useState([]);
  const [loadingMine, setLoadingMine] = useState(true);

  const loadMine = useCallback(async () => {
    if (!accessToken) return;
    setLoadingMine(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/social/communities/mine`, {
        headers: { Authorization: `Bearer ${accessToken}` },
        params: { limit: 40 },
      });
      setMine(res.data?.communities || []);
    } catch (err) {
      console.error("Failed to load Your communities:", err);
      setMine([]);
    } finally {
      setLoadingMine(false);
    }
  }, [accessToken]);

  useEffect(() => {
    loadMine();
  }, [loadMine]);

  const handle = profile?.username;
  const profileHref = handle ? paths.user(handle) : paths.profileEdit();
  const savedHref = handle
    ? `${paths.user(handle)}?tab=saved`
    : paths.profileEdit();
  const draftsHref = handle
    ? `${paths.user(handle)}?tab=drafts`
    : paths.profileEdit();

  return (
    <aside
      className={cn(
        "hidden xl:block w-[220px] shrink-0 min-w-0 sticky top-4 self-start max-h-[calc(100vh-6rem)] overflow-y-auto overflow-x-hidden space-y-6 pr-1",
        className
      )}
    >
      {complete && profile ? (
        <Link
          to={profileHref}
          className="flex items-center gap-3 min-w-0 rounded-md p-1 -m-1 hover:bg-muted/50 transition-colors"
        >
          <SocialUserAvatar
            user={profile}
            avatarUrl={profile.avatar_url}
            displayName={profile.display_name}
            className="h-10 w-10 shrink-0"
            fallbackClassName="bg-primary/10 text-primary text-xs"
          />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold truncate" title={profile.display_name || "You"}>
              {profile.display_name || "You"}
            </p>
            {handle ? (
              <p className="text-xs text-muted-foreground truncate" title={`@${handle}`}>
                @{handle}
              </p>
            ) : null}
          </div>
        </Link>
      ) : null}

      <nav className="space-y-1">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground px-1">
          Shortcuts
        </p>
        <Link
          to={savedHref}
          className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted/50 transition-colors"
        >
          <Bookmark className="h-4 w-4 shrink-0 text-muted-foreground" />
          Saved
        </Link>
        <Link
          to={draftsHref}
          className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted/50 transition-colors"
        >
          <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
          Drafts
        </Link>
      </nav>

      {!loadingMine && mine.length > 0 ? (
        <section className="space-y-2">
          <h3 className="min-w-0 truncate text-xs font-semibold uppercase tracking-wide text-muted-foreground px-1">
            Your communities
          </h3>
          <ul className="space-y-1.5 max-h-56 overflow-y-auto overflow-x-hidden pr-1">
            {mine.map((c) => (
              <li key={c.community_id} className="min-w-0">
                <Link
                  to={paths.community(c.slug || c.community_id)}
                  className="flex items-center gap-1 min-w-0 max-w-full px-1 text-sm hover:underline"
                >
                  <span className="truncate" title={c.display_name}>
                    {c.display_name}
                  </span>
                  <VerifiedOrgMark
                    org={c}
                    title="Verified community"
                    className="h-3 w-3 shrink-0"
                  />
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </aside>
  );
}
